import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../hooks/use-toast';
import api from '../../services/api';
import { Plus, Pencil, Calendar, Users, MapPin, Loader2, Globe2, Building2, BarChart3 } from 'lucide-react';

const STATUS_LABEL = {
  borrador: 'Borrador', revision: 'En revisión', aprobado: 'Aprobado',
  publicado: 'Publicado', cancelado: 'Cancelado', finalizado: 'Finalizado',
};
const NEXT_STATUS = {
  borrador: ['revision', 'cancelado'],
  revision: ['aprobado', 'borrador', 'cancelado'],
  aprobado: ['publicado', 'borrador', 'cancelado'],
  publicado: ['finalizado', 'cancelado'],
  cancelado: [], finalizado: [],
};
const EMPTY = {
  title: '', description: '', event_type: 'presencial', date: '', time: '', location: '',
  price: 0, total_spots: 100, scope: 'SECCION', section_id: '', section_ids: [], visibility: 'afiliados',
};

const AdminEventos = () => {
  const [events, setEvents] = useState([]);
  const [ctx, setCtx] = useState(null);
  const [resumen, setResumen] = useState(null);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(EMPTY);
  const { toast } = useToast();

  const isNational = ctx?.accessible_scope === 'ALL';
  const sections = ctx?.accessible_sections || [];

  const loadAll = async () => {
    try {
      const [c, e, r] = await Promise.all([
        api.get('/org/me/context'),
        api.get('/events/manage'),
        api.get('/events/manage/resumen'),
      ]);
      setCtx(c.data);
      setEvents(e.data);
      setResumen(r.data);
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'No se pudieron cargar los eventos', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (ctx && !isNational && !formData.section_id && sections.length > 0 && !editing) {
      setFormData((f) => ({ ...f, scope: 'SECCION', section_id: sections[0].id }));
    }
  }, [ctx]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      title: formData.title,
      description: formData.description,
      event_type: formData.event_type,
      date: formData.date ? new Date(`${formData.date}T${formData.time || '09:00'}:00`).toISOString() : null,
      time: formData.time,
      location: formData.location,
      price: parseFloat(formData.price) || 0,
      total_spots: parseInt(formData.total_spots, 10) || null,
      visibility: formData.visibility,
      scope: formData.scope,
      section_id: formData.scope === 'SECCION' ? formData.section_id : null,
      section_ids: formData.scope === 'MULTISECCION' ? formData.section_ids : null,
    };
    try {
      if (editing) {
        await api.put(`/events/manage/${editing.id}`, payload);
        toast({ title: 'Evento actualizado' });
      } else {
        await api.post('/events/manage', payload);
        toast({ title: 'Evento creado en borrador' });
      }
      resetForm();
      loadAll();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Error al guardar', variant: 'destructive' });
    }
  };

  const transition = async (ev, to) => {
    try {
      await api.post(`/events/manage/${ev.id}/transition`, { to_status: to });
      toast({ title: `Evento: ${STATUS_LABEL[to]}` });
      loadAll();
    } catch (error) {
      toast({ title: 'No permitido', description: error.response?.data?.detail || 'Transición rechazada', variant: 'destructive' });
    }
  };

  const loadStats = async (ev) => {
    try {
      const r = await api.get(`/events/manage/${ev.id}/stats`);
      setStats((s) => ({ ...s, [ev.id]: r.data }));
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Sin acceso', variant: 'destructive' });
    }
  };

  const handleEdit = (ev) => {
    setEditing(ev);
    setFormData({
      title: ev.title || '', description: ev.description || '', event_type: ev.event_type || 'presencial',
      date: ev.date ? ev.date.split('T')[0] : '', time: ev.time || '', location: ev.location || '',
      price: ev.price || 0, total_spots: ev.total_spots || 100, scope: ev.scope || 'SECCION',
      section_id: ev.section_id || '', section_ids: ev.section_ids || [], visibility: ev.visibility || 'afiliados',
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditing(null);
    setFormData({ ...EMPTY, section_id: !isNational && sections[0] ? sections[0].id : '' });
  };

  const toggleMulti = (id) => setFormData((f) => ({
    ...f,
    section_ids: f.section_ids.includes(id) ? f.section_ids.filter((x) => x !== id) : [...f.section_ids, id],
  }));

  const sectionName = (id) => sections.find((s) => s.id === id)?.abbreviation || 'Sección';

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-eventos">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Eventos</h1>
            <p className="text-base text-gray-600">Eventos nacionales y territoriales por sección</p>
          </div>
          <Button data-testid="evento-nuevo-btn" onClick={() => { resetForm(); setShowForm(true); }} className="bg-gray-900">
            <Plus className="h-4 w-4 mr-2" /> Nuevo Evento
          </Button>
        </div>

        {resumen && (
          <Card data-testid="eventos-resumen">
            <CardContent className="py-4 flex flex-wrap gap-6 text-sm">
              <span data-testid="resumen-eventos"><strong>{resumen.eventos}</strong> eventos</span>
              <span data-testid="resumen-registros"><strong>{resumen.registros}</strong> registros</span>
              <span data-testid="resumen-asistencia"><strong>{resumen.asistencia}</strong> asistencias</span>
              <span data-testid="resumen-espera"><strong>{resumen.lista_espera}</strong> en lista de espera</span>
              <span data-testid="resumen-pagos" className="text-amber-700">Pagos de eventos: bloqueados hasta Fase 3</span>
            </CardContent>
          </Card>
        )}

        {showForm && (
          <Card>
            <CardHeader><CardTitle>{editing ? 'Editar Evento' : 'Nuevo Evento'}</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="evento-form">
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Alcance</label>
                    <select data-testid="evento-scope" className="w-full rounded-md border border-gray-300 p-2"
                      value={formData.scope} onChange={(e) => setFormData({ ...formData, scope: e.target.value })}>
                      <option value="SECCION">Sección (territorial)</option>
                      {isNational && <option value="NACIONAL">Nacional</option>}
                      {isNational && <option value="MULTISECCION">Multisección</option>}
                    </select>
                    {!isNational && <p className="text-xs text-gray-500 mt-1">Solo un rol nacional puede crear eventos nacionales o multisección.</p>}
                  </div>
                  {formData.scope === 'SECCION' && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Sección</label>
                      <select data-testid="evento-section" className="w-full rounded-md border border-gray-300 p-2"
                        value={formData.section_id} onChange={(e) => setFormData({ ...formData, section_id: e.target.value })} required>
                        <option value="">Selecciona…</option>
                        {sections.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.abbreviation})</option>)}
                      </select>
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium text-gray-700">Visibilidad</label>
                    <select data-testid="evento-visibility" className="w-full rounded-md border border-gray-300 p-2"
                      value={formData.visibility} onChange={(e) => setFormData({ ...formData, visibility: e.target.value })}>
                      <option value="afiliados">Afiliados</option>
                      <option value="publico">Público</option>
                      <option value="privado">Privado</option>
                    </select>
                  </div>
                </div>

                {formData.scope === 'MULTISECCION' && (
                  <div data-testid="evento-multiseccion">
                    <label className="text-sm font-medium text-gray-700">Secciones participantes (mínimo 2)</label>
                    <div className="flex flex-wrap gap-3 mt-2">
                      {sections.map((s) => (
                        <label key={s.id} className="flex items-center gap-2 text-sm border rounded-lg px-3 py-1.5">
                          <input type="checkbox" data-testid={`evento-multi-${s.abbreviation}`}
                            checked={formData.section_ids.includes(s.id)} onChange={() => toggleMulti(s.id)} />
                          {s.abbreviation}
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Título</label>
                    <Input data-testid="evento-title" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Modalidad</label>
                    <select data-testid="evento-modalidad" className="w-full rounded-md border border-gray-300 p-2"
                      value={formData.event_type} onChange={(e) => setFormData({ ...formData, event_type: e.target.value })}>
                      <option value="presencial">Presencial</option>
                      <option value="online">En línea</option>
                      <option value="hybrid">Híbrido</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">Descripción</label>
                  <textarea data-testid="evento-desc" className="w-full rounded-md border border-gray-300 p-2" rows={3}
                    value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
                </div>

                <div className="grid md:grid-cols-4 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Fecha</label>
                    <Input data-testid="evento-date" type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} required />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Hora</label>
                    <Input data-testid="evento-time" value={formData.time} onChange={(e) => setFormData({ ...formData, time: e.target.value })} placeholder="10:00" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Precio (MXN)</label>
                    <Input data-testid="evento-price" type="number" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} />
                    <p className="text-[11px] text-amber-700 mt-1">Con precio &gt; 0 el registro queda en espera de Fase 3.</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Cupo</label>
                    <Input data-testid="evento-cupo" type="number" value={formData.total_spots} onChange={(e) => setFormData({ ...formData, total_spots: e.target.value })} />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">Sede / enlace</label>
                  <Input data-testid="evento-location" value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} />
                </div>

                <div className="flex justify-end space-x-3">
                  <Button type="button" variant="outline" data-testid="evento-cancelar" onClick={resetForm}>Cancelar</Button>
                  <Button type="submit" data-testid="evento-guardar" className="bg-gray-900">{editing ? 'Actualizar' : 'Crear'}</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-gray-400" /></div>
        ) : events.length === 0 ? (
          <Card><CardContent className="py-12 text-center">
            <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500" data-testid="eventos-vacio">No hay eventos en tu alcance</p>
          </CardContent></Card>
        ) : (
          <div className="grid gap-4" data-testid="eventos-lista">
            {events.map((ev) => (
              <Card key={ev.id} data-testid={`evento-card-${ev.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <Badge data-testid={`evento-scope-${ev.id}`} className={ev.scope === 'NACIONAL' ? 'bg-indigo-100 text-indigo-700' : 'bg-emerald-100 text-emerald-700'}>
                          {ev.scope === 'NACIONAL' ? <><Globe2 className="h-3 w-3 mr-1" /> Nacional</> : <><Building2 className="h-3 w-3 mr-1" /> {ev.scope === 'MULTISECCION' ? 'Multisección' : sectionName(ev.section_id)}</>}
                        </Badge>
                        <Badge data-testid={`evento-status-${ev.id}`} variant="outline">{STATUS_LABEL[ev.status] || ev.status}</Badge>
                        <Badge variant="outline">{ev.visibility}</Badge>
                        {ev.payment_blocked && <Badge className="bg-amber-100 text-amber-800">Cobro bloqueado (Fase 3)</Badge>}
                      </div>
                      <h3 className="text-lg font-bold text-gray-900">{ev.title}</h3>
                      <p className="text-sm text-gray-600 mt-1">{ev.description}</p>
                      <div className="flex flex-wrap gap-4 mt-3 text-sm text-gray-500">
                        <span className="flex items-center"><Calendar className="h-4 w-4 mr-1" />{ev.date ? new Date(ev.date).toLocaleDateString('es-MX') : 'Sin fecha'}{ev.time ? ` · ${ev.time}` : ''}</span>
                        {ev.location && <span className="flex items-center"><MapPin className="h-4 w-4 mr-1" />{ev.location}</span>}
                        <span className="flex items-center"><Users className="h-4 w-4 mr-1" />{ev.available_spots ?? '—'}/{ev.total_spots ?? '—'} lugares</span>
                      </div>
                      {stats[ev.id] && (
                        <p data-testid={`evento-stats-${ev.id}`} className="text-sm text-gray-700 mt-3">
                          Registros: <strong>{stats[ev.id].registros}</strong> · Asistencia: <strong>{stats[ev.id].asistencia}</strong> · Lista de espera: <strong>{stats[ev.id].lista_espera}</strong>
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col gap-2 w-56">
                      {ev.can_edit && (
                        <Button variant="outline" size="sm" data-testid={`evento-editar-${ev.id}`} onClick={() => handleEdit(ev)}>
                          <Pencil className="h-4 w-4 mr-2" /> Editar
                        </Button>
                      )}
                      <Button variant="outline" size="sm" data-testid={`evento-stats-btn-${ev.id}`} onClick={() => loadStats(ev)}>
                        <BarChart3 className="h-4 w-4 mr-2" /> Registros
                      </Button>
                      {ev.can_edit && (NEXT_STATUS[ev.status] || []).map((to) => (
                        <Button key={to} size="sm" variant={to === 'publicado' ? 'default' : 'outline'}
                          className={to === 'publicado' ? 'bg-gray-900' : ''}
                          data-testid={`evento-transicion-${to}-${ev.id}`} onClick={() => transition(ev, to)}>
                          {to === 'revision' ? 'Enviar a revisión' : to === 'aprobado' ? 'Aprobar' : to === 'publicado' ? 'Publicar' : to === 'borrador' ? 'Regresar a borrador' : to === 'cancelado' ? 'Cancelar evento' : 'Finalizar'}
                        </Button>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminEventos;
